import { apiGet, apiPatch } from './client';

export interface EmployeeInvoiceSummary {
  month: number;
  year: number;
  state: 'open' | 'sent';
  totalHours: number;
  totalBilling: number;
  totalExpenses: number;
  totalExtraCosts: number;
  totalAmount: number;
  netAmount: number;
}

export interface EmployeeInvoiceProjectBreakdown {
  projectId: number;
  projectName: string;
  hours: number;
  rate: number;
  amount: number;
}

export interface EmployeeInvoiceExpense {
  expenseSheetId: number;
  projectName: string | null;
  description: string | null;
  amount: number;
}

export interface ExtraCostDeductionLine {
  extraCostId: number;
  description: string;
  costDate: string;
  totalAmount: number;
  appliedAmount: number;
  priorApplied: number;
  remaining: number;
}

/**
 * MKS-125 — advisory shown on an OPEN invoice whose deductions are cascading
 * from an earlier month that was never marked Sent. `earlierOpenMonths` is
 * ordered chronologically.
 */
export interface InvoiceWarning {
  type: 'earlier-months-open';
  earlierOpenMonths: { year: number; month: number }[];
}

export interface EmployeeInvoiceDetail {
  id: number | null;
  month: number;
  year: number;
  state: 'open' | 'sent';
  totalHours: number;
  totalBilling: number;
  totalExpenses: number;
  totalExtraCosts: number;
  totalAmount: number;
  netAmount: number;
  byProject: EmployeeInvoiceProjectBreakdown[];
  expenses: EmployeeInvoiceExpense[];
  extraCosts: ExtraCostDeductionLine[];
  carryOver: number;
  // Optional: older detail sources (e.g. some admin/freelancer paths) may omit it.
  warning?: InvoiceWarning | null;
  // Phase 84 — a person in concurrent employment now sees their OWN salary
  // deduction and the resulting figure to invoice. Null when no salary is
  // recorded for the month. Never carries anyone else's figures: the route is
  // scoped to the session user.
  salaryAmount?: number | null;
  deductedField?: 'net' | 'gross' | 'total_cost' | 'none' | null;
  /** netAmount minus the deducted salary, floored at 0. */
  invoice?: number;
}

export async function getEmployeeInvoices(limit?: number): Promise<EmployeeInvoiceSummary[]> {
  const params = limit ? `?limit=${limit}` : '';
  const res = await apiGet<{ data: EmployeeInvoiceSummary[] }>(`/employee-invoices${params}`);
  return res.data;
}

export async function getEmployeeInvoiceDetail(year: number, month: number): Promise<EmployeeInvoiceDetail> {
  const res = await apiGet<{ data: EmployeeInvoiceDetail }>(`/employee-invoices/${year}/${month}`);
  return res.data;
}

export async function updateInvoiceState(year: number, month: number, state: 'open' | 'sent'): Promise<{ month: number; year: number; state: string }> {
  const res = await apiPatch<{ data: { month: number; year: number; state: string } }>(`/employee-invoices/${year}/${month}/state`, { state });
  return res.data;
}
